import type { Client, SubscribePayload } from 'graphql-ws';
import {
	type CacheConfig,
	type GraphQLResponse,
	Observable,
	type RequestParameters,
	type SubscribeFunction,
	type Variables,
} from 'relay-runtime';
import type { Sink } from 'relay-runtime/lib/network/RelayObservable';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { getSubscriptionUrl } from '@atlassian/graphql-subscription/subscription-sharding-context';
import {
	acquireSubscriptionClient,
	getClient,
	getSocketConnectionState,
} from '@atlassian/graphql-subscription/utils/client';
import { getSinkError, type SinkError } from '@atlassian/graphql-subscription/utils/sink';

import type {
	SubscriptionDiagnosticsEvent,
	SubscriptionDiagnosticsListener,
} from './subscription-diagnostics';

export type SubscribeOptions = {
	subscribeExtensions?: Record<string, string>;
	onSubscribeTrigger?: (payload: SubscribePayload) => void;
	/** Product-level observer lifecycle reporter; variables are for correlation, not logging. */
	onSubscriptionDiagnostic?: SubscriptionDiagnosticsListener;
	/** Attach shape at subscribe time: opener vs join, and observer count on that channel. */
	onSubscribeError?: (
		payload: SubscribePayload & {
			requestId: string | null;
			isNewChannel: boolean;
			observerCount: number;
		},
	) => void;
};

// One upstream sub per channel (AFB-2118); fan out to local observers.
type LocalSink = {
	next: (value: unknown) => void;
	// requestId captured before the channel is dropped.
	error: (err: unknown, requestId: string | null) => void;
	complete?: () => void;
};

type Channel = {
	sinks: Set<LocalSink>;
	dispose: () => void;
	lastRequestId: string | null;
	hasReceivedResponse: boolean;
};

const channels = new Map<string, Channel>();
const channelsByClient = new WeakMap<Client, Map<string, Channel>>();

type Cleanup = (() => void) | void;

/** Select connection state; all observers then run the same subscription implementation. */
function createSubscriptionObservable(
	cacheConfig: CacheConfig,
	subscribe: (
		sink: Sink<GraphQLResponse>,
		client: Client | null,
		channels: Map<string, Channel>,
		getConnectionState: typeof getSocketConnectionState,
	) => Cleanup,
): Observable<GraphQLResponse> {
	return Observable.create((sink) => {
		const context = cacheConfig?.metadata?.subscriptionShardingContext;
		if (context === undefined || !fg('platform_relay_subscription_sharding')) {
			return subscribe(sink, getClient(), channels, getSocketConnectionState);
		}

		const lease = acquireSubscriptionClient(() => getSubscriptionUrl(context));
		if (!lease) {
			return subscribe(sink, null, channels, getSocketConnectionState);
		}
		try {
			let clientChannels = channelsByClient.get(lease.client);
			if (!clientChannels) {
				clientChannels = new Map();
				channelsByClient.set(lease.client, clientChannels);
			}
			const cleanup = subscribe(sink, lease.client, clientChannels, lease.getSocketConnectionState);
			return () => {
				try {
					cleanup?.();
				} finally {
					lease.release();
				}
			};
		} catch (error) {
			lease.release();
			throw error;
		}
	});
}

function stableStringify(value: unknown): string {
	if (value === null || typeof value !== 'object') {
		return JSON.stringify(value) ?? 'null';
	}
	if (Array.isArray(value)) {
		return `[${value.map(stableStringify).join(',')}]`;
	}
	const obj = value as Record<string, unknown>;
	const keys = Object.keys(obj).sort();
	return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
}

function channelKey(payload: SubscribePayload): string {
	return stableStringify({
		pq: (payload.extensions as Record<string, unknown> | undefined)?.pq ?? null,
		operationName: payload.operationName ?? null,
		variables: payload.variables ?? null,
		extensions: payload.extensions ?? null,
	});
}

let cachedDefaultSubscribe: SubscribeFunction | undefined;

export function createSubscribeFn(subscribeOptions?: SubscribeOptions): SubscribeFunction;
export function createSubscribeFn(subscribeOptions?: SubscribeOptions): SubscribeFunction {
	if (typeof subscribeOptions === 'undefined' && typeof cachedDefaultSubscribe !== 'undefined') {
		return cachedDefaultSubscribe;
	}

	const fn: SubscribeFunction = (
		{ id, name: operationName }: RequestParameters,
		variables: Variables,
		cacheConfig: CacheConfig,
	) =>
		createSubscriptionObservable(cacheConfig, (sink, client, channels, getConnectionState) => {
			if (!client) {
				return sink.error(new Error('Unable to create subscription client'));
			}
			const replayConfig = cacheConfig?.metadata?.replay || undefined;

			const replayCommand =
				replayConfig !== null && typeof replayConfig === 'object' && 'command' in replayConfig
					? (replayConfig as Record<string, unknown>).command
					: undefined;
			const replayExtensions = replayCommand
				? { command: replayCommand }
				: replayConfig
					? { replay: replayConfig }
					: {};

			const payload: SubscribePayload = {
				query: '',
				operationName,
				variables,
				extensions: {
					pq: id,
					...(subscribeOptions?.subscribeExtensions || {}),
					...replayExtensions,
				},
			};

			subscribeOptions?.onSubscribeTrigger?.(payload);

			const key = channelKey(payload);
			// Keep one cleanup policy for the entire observer lifetime.
			const guardChannelOwnership = !fg('relay_subscription_cleanup_killswitch');

			const existingChannel = channels.get(key);
			let joinedChannel = existingChannel;
			// Capture before failure; the channel may reopen first.
			const isNewChannel = !existingChannel;
			const observerCount = (existingChannel?.sinks.size ?? 0) + 1;
			// Reuse the product-level subscription reporter instead of a per-call metadata callback.
			const diagnosticsEnabled = fg('platform_relay_subscription_reliability-additional');
			const diagnostics =
				diagnosticsEnabled && typeof subscribeOptions?.onSubscriptionDiagnostic === 'function'
					? subscribeOptions.onSubscriptionDiagnostic
					: undefined;
			const report = (event: SubscriptionDiagnosticsEvent): void => {
				try {
					diagnostics?.({
						operationName: payload.operationName,
						variables: payload.variables,
						...event,
					});
				} catch {
					// Diagnostics must not interrupt subscriptions.
				}
			};
			if (diagnostics) {
				report({
					type: 'attached',
					isNewChannel,
					observerCount,
					channelHasReceivedResponse: existingChannel?.hasReceivedResponse ?? false,
					socketState: getConnectionState(),
				});
			}
			const removeConnected = diagnostics
				? client.on('connected', () => report({ type: 'connected' }))
				: undefined;
			const removeClosed = diagnostics
				? client.on('closed', () => report({ type: 'closed' }))
				: undefined;
			const detachDiagnostics = (): void => {
				removeConnected?.();
				removeClosed?.();
			};

			const localSink: LocalSink = {
				next: (value) => {
					if (diagnostics) {
						const extensions = (
							value as {
								extensions?: {
									gateway?: { request_id?: unknown; tId?: unknown; trace_id?: unknown };
									agg?: { channel?: unknown };
								};
							} | null
						)?.extensions;
						const requestId = extensions?.gateway?.request_id;
						const traceId = extensions?.gateway?.tId ?? extensions?.gateway?.trace_id;
						const channel = extensions?.agg?.channel;
						report({
							type: 'response',
							requestId: typeof requestId === 'string' ? requestId : null,
							traceId: typeof traceId === 'string' ? traceId : null,
							channel: typeof channel === 'string' ? channel : null,
						});
					}
					// @ts-expect-error incompatible types between two libraries
					sink.next(value);
				},
				error: (err, requestId) => {
					report({ type: 'error', requestId });
					detachDiagnostics();
					sink.error(err as Error);
					subscribeOptions?.onSubscribeError?.({
						...payload,
						requestId,
						isNewChannel,
						observerCount,
					});
				},
				complete: () => {
					report({ type: 'completed' });
					detachDiagnostics();
					sink.complete?.();
				},
			};

			if (!existingChannel) {
				const newChannel: Channel = {
					sinks: new Set([localSink]),
					dispose: () => {},
					lastRequestId: null,
					hasReceivedResponse: false,
				};
				joinedChannel = newChannel;
				let terminated = false;

				newChannel.dispose = client.subscribe(payload, {
					next: (value) => {
						if (diagnosticsEnabled) {
							newChannel.hasReceivedResponse = true;
						}
						// @ts-expect-error agg prop is missing on generic "extensions" type
						const aggRequestId = value?.extensions?.gateway?.request_id;
						if (aggRequestId) {
							newChannel.lastRequestId = aggRequestId;
						}
						for (const s of Array.from(newChannel.sinks)) {
							try {
								s.next(value);
							} catch {
								/* isolate sinks */
							}
						}
					},
					error: (sinkError: SinkError) => {
						terminated = true;
						const errorInfo = getSinkError(sinkError);
						const sinks = Array.from(newChannel.sinks);
						const { lastRequestId } = newChannel;
						if (!guardChannelOwnership || channels.get(key) === newChannel) {
							channels.delete(key);
						}
						newChannel.sinks.clear();
						for (const s of sinks) {
							try {
								s.error(errorInfo, lastRequestId);
							} catch {
								/* isolate sinks */
							}
						}
					},
					complete: () => {
						terminated = true;
						const sinks = Array.from(newChannel.sinks);
						if (!guardChannelOwnership || channels.get(key) === newChannel) {
							channels.delete(key);
						}
						newChannel.sinks.clear();
						for (const s of sinks) {
							try {
								s.complete?.();
							} catch {
								/* isolate sinks */
							}
						}
					},
				});

				// A synchronous terminal callback may already have opened a replacement.
				// Register only a live channel, once its real disposer is available.
				if (!guardChannelOwnership || !terminated) {
					channels.set(key, newChannel);
				}
			} else {
				existingChannel.sinks.add(localSink);
			}

			return () => {
				report({ type: 'disposed' });
				detachDiagnostics();
				const c = guardChannelOwnership ? joinedChannel : channels.get(key);
				if (!c) {
					return;
				}
				const removed = c.sinks.delete(localSink);
				if (guardChannelOwnership && !removed) {
					return;
				}
				if (c.sinks.size === 0) {
					if (!guardChannelOwnership || channels.get(key) === c) {
						channels.delete(key);
					}
					try {
						c.dispose();
					} catch {
						/* upstream dispose errors must not break Relay teardown */
					}
				}
			};
		});

	if (typeof subscribeOptions === 'undefined') {
		cachedDefaultSubscribe = fn;
	}

	return fn;
}

export const subscribe: SubscribeFunction = createSubscribeFn();
